import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SidebarLink as SidebarLinkType } from "./types";
import { 
  Home, 
  Users, 
  Gamepad2, 
  HelpCircle, 
  Settings, 
  Calendar, 
  Clock, 
  ShoppingCart,
  Crosshair,
  BookOpen,
  CreditCard,
  UserRound,
  Wallet,
  MessageSquare,
  DollarSign,
  TrendingUp,
  Group
} from "lucide-react";

interface SidebarLinkProps {
  link: SidebarLinkType;
  collapsed: boolean;
}

export const SidebarLink: React.FC<SidebarLinkProps> = ({ link, collapsed }) => {
  const location = useLocation();
  
  const isLinkActive = (to: string) => {
    if (to === "/" && location.pathname !== "/") {
      return false;
    }
    
    return to === location.pathname || 
           (to !== "/" && location.pathname.startsWith(to));
  };
  
  const isActive = isLinkActive(link.to);

  // Function to render the appropriate icon
  const renderIcon = (iconName: string) => {
    switch(iconName) {
      case 'home':
        return <Home className="h-5 w-5" />;
      case 'users':
        return <Users className="h-5 w-5" />;
      case 'gamepad':
        return <Gamepad2 className="h-5 w-5" />;
      case 'help':
        return <HelpCircle className="h-5 w-5" />;
      case 'settings':
        return <Settings className="h-5 w-5" />;
      case 'calendar':
        return <Calendar className="h-5 w-5" />;
      case 'clock':
        return <Clock className="h-5 w-5" />;
      case 'shopping':
        return <ShoppingCart className="h-5 w-5" />;
      case 'crosshair':
        return <Crosshair className="h-5 w-5" />;
      case 'book':
        return <BookOpen className="h-5 w-5" />;
      case 'creditcard':
        return <CreditCard className="h-5 w-5" />;
      case 'user':
        return <UserRound className="h-5 w-5" />;
      case 'wallet':
        return <Wallet className="h-5 w-5" />;
      case 'message':
        return <MessageSquare className="h-5 w-5" />;
      case 'dollar':
        return <DollarSign className="h-5 w-5" />;
      case 'trending':
        return <TrendingUp className="h-5 w-5" />;
      case 'group':
        return <Group className="h-5 w-5" />;
      default:
        return <HelpCircle className="h-5 w-5" />;
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={link.to}
            className={`flex items-center gap-3 px-3 py-2.5 mb-1 rounded-lg transition-all ${
              isActive 
                ? link.section === 'app' 
                  ? "bg-gradient-to-r from-[#182437] to-[#1E293B] text-[#00B6F0] border-l-4 border-[#00B6F0]" 
                  : "bg-gradient-to-r from-[#182437] to-[#1E293B] text-[#00B6F0]" 
                : "text-gray-400 hover:text-white hover:bg-[#1A2233]"
            }`}
          >
            <span className={`${isActive ? "text-[#00B6F0]" : "text-gray-400"} flex-shrink-0`}>
              {renderIcon(link.icon)}
            </span>
            {!collapsed && (
              <span className="font-medium truncate">{link.label}</span>
            )}
            {isActive && !collapsed && link.section === 'app' && (
              <span className="ml-auto h-2 w-2 rounded-full bg-[#00B6F0]"></span>
            )}
          </Link>
        </TooltipTrigger>
        {collapsed && (
          <TooltipContent side="right">
            {link.label}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};
